import React from 'react';
import { Alert, Skeleton } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { AppVersionCounts, getAppVersions } from '../../services/connectConfig';
import { activeDevicesBelow } from './appVersions';

type ConnectEnvironment = 'dev' | 'prod';
type Platform = 'android' | 'ios';

/** Shared with the policy drawer, which shows how many devices a cutoff blocks. */
export function useAppVersions(environment: ConnectEnvironment) {
  return useQuery({
    queryKey: ['appVersions', environment],
    queryFn: () => getAppVersions(environment),
    retry: false,
  });
}

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

function countsLabel(counts: AppVersionCounts): string {
  if (counts.devices === 0) return '—';
  return `${plural(counts.devices, 'device')} · ${plural(counts.users, 'user')}`;
}

/**
 * Which builds devices run, split into active and inactive. Used to decide
 * when a build can be force-updated and its migration code removed; inactive
 * devices are listed separately so they don't hold that back.
 */
const AppVersionsSection: React.FC<{
  environment: ConnectEnvironment;
  platform: Platform;
  platformName: string;
  threshold: number;
}> = ({ environment, platform, platformName, threshold }) => {
  const query = useAppVersions(environment);
  const summary = query.data?.platforms[platform];
  const windowDays = query.data?.activeWindowDays ?? 30;
  const blocked = activeDevicesBelow(summary, threshold);
  const unreportedActive = summary?.unreported.active.devices ?? 0;

  return (
    <section className="release-ledger release-versions" aria-labelledby="release-versions-title">
      <div className="release-section-intro release-ledger-intro">
        <div>
          <span>INSTALLED BUILDS</span>
          <h3 id="release-versions-title">Which {platformName} builds devices run</h3>
          <p>
            Active means signed in and seen in the last {windowDays} days. Retire a build
            once no active device needs it, then remove its migration code.
          </p>
        </div>
        {query.data && (
          <small>Updated {new Date(query.data.generatedAt).toLocaleString('en')}</small>
        )}
      </div>

      {query.isLoading ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : query.isError ? (
        <Alert
          type="warning"
          showIcon
          message="Build counts are temporarily unavailable"
          description={(query.error as any)?.response?.data?.error || 'Check the Connect deployment and try again.'}
        />
      ) : !summary || (summary.builds.length === 0 && unreportedActive === 0) ? (
        <div className="release-ledger-empty">
          <p>No {platformName} devices have reported a build yet.</p>
        </div>
      ) : (
        <>
          <div className="release-versions-facts">
            <div>
              <span>Oldest build on an active device</span>
              <strong>{summary.oldestActiveBuild ?? '—'}</strong>
            </div>
            <div>
              <span>Active devices below the current cutoff</span>
              <strong>{threshold > 0 ? blocked : '—'}</strong>
            </div>
            {unreportedActive > 0 && (
              <div>
                <span>Active devices on builds too old to report</span>
                <strong>{unreportedActive}</strong>
              </div>
            )}
          </div>
          <div className="release-ledger-list">
            <div className="release-versions-row release-versions-head">
              <span>Build</span>
              <span>Active</span>
              <span>Inactive</span>
            </div>
            {summary.builds.map((build) => (
              <div
                className={`release-versions-row ${threshold > 0 && build.buildNumber < threshold ? 'is-below-cutoff' : ''}`}
                key={build.buildNumber}
              >
                <div className="release-ledger-version">
                  <strong>{build.appVersion || 'Version unknown'}</strong>
                  <span>Build {build.buildNumber}</span>
                </div>
                <span>{countsLabel(build.active)}</span>
                <span>{countsLabel(build.inactive)}</span>
              </div>
            ))}
            {(unreportedActive > 0 || (summary.unreported.inactive.devices ?? 0) > 0) && (
              <div className="release-versions-row">
                <div className="release-ledger-version">
                  <strong>Older builds</strong>
                  <span>From before build reporting</span>
                </div>
                <span>{countsLabel(summary.unreported.active)}</span>
                <span>{countsLabel(summary.unreported.inactive)}</span>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default AppVersionsSection;
